import { AfterViewInit, Component, ElementRef, ViewChild, Input, OnDestroy, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription, fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

@Component({
  selector: 'app-banner-carousel',
  templateUrl: './banner-carousel.component.html',
  styleUrls: ['./banner-carousel.component.scss']
})
export class BannerCarouselComponent implements AfterViewInit, OnDestroy {
  @Input() images: string[] = [];
  @Input('interval-in-seconds') intervalInSeconds: number = 5;
  @ViewChild('carousel') private carousel!: ElementRef;

  private scrollSubscription!: Subscription;
  private manualScrollSubscription!: Subscription;
  private resizeSubscription!: Subscription;
  private carouselPosition: number = 0;
  private lastScrollTime: number = 0;
  private isManualScroll = false;
  private isMobile = false;

  constructor(
    private renderer: Renderer2,
  ) { }

  ngAfterViewInit(): void {
    
    this.intervalInSeconds = this.intervalInSeconds * 1000;

    this.setupCarousel();
  }

  private setupCarousel(): void {
    this.setupAutoScroll();
    this.setupManualScrollDetection();
    this.setupResizeHandler();
  }

  private checkIfMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private setupAutoScroll(): void {
    this.scrollSubscription = interval(this.intervalInSeconds).subscribe(() => {
      this.scrollToIndex((this.carouselPosition + 1) % this.images.length);
    });
  }

  private setupManualScrollDetection(): void {
    this.manualScrollSubscription = fromEvent(this.carousel.nativeElement, 'scroll')
      .pipe(throttleTime(100))
      .subscribe(() => {
        if (this.isManualScroll) return;
        this.updateCurrentPosition();
      });
  }

  private updateCurrentPosition(): void {
    const itemWidth = this.carousel.nativeElement.querySelector('.banner-image-container')?.clientWidth || 0;
    const scrollLeft = this.carousel.nativeElement.scrollLeft;

    this.carouselPosition = Math.round(scrollLeft / itemWidth);
    this.resetInterval();
  }

  private scrollToIndex(index: number): void {
    if (this.carousel?.nativeElement && this.images.length > 0) {
      this.isManualScroll = true;
      this.resetInterval();
      this.carouselPosition = index;

      const itemWidth = this.carousel.nativeElement.querySelector('.banner-image-container')?.clientWidth || 0;
      const carouselStyles = window.getComputedStyle(this.carousel.nativeElement);
      const gapPercentage = parseFloat(carouselStyles.gap) || 0;
      const gapValue = this.carousel.nativeElement.clientWidth * (gapPercentage / 100);
      const scrollPosition = index * (itemWidth + gapValue);

      this.carousel.nativeElement.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });

      const onScrollEnd = () => {
        this.isManualScroll = false;
        this.carousel.nativeElement.removeEventListener('scrollend', onScrollEnd);
      };
      this.carousel.nativeElement.addEventListener('scrollend', onScrollEnd);
    }
  }

  private setupResizeHandler(): void {
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(throttleTime(200))
      .subscribe(() => {
        this.scrollToIndex(this.carouselPosition);
      });
  }

  private resetInterval(): void {
    this.scrollSubscription?.unsubscribe();
    this.setupAutoScroll();
  }

  ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
    this.manualScrollSubscription?.unsubscribe();
    this.resizeSubscription?.unsubscribe();
  }

  getUrl(url: any) {
    window.open(url, '_blank');
  }
}
